import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(":", ".")
      .replace(/\s+/g, " ")
      .trim();

    return `${datePart} ${timePart}`;
  } catch {
    return String(value);
  }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "-";
  const formatDateOnly = (value) => {
    if (!value) return "";

    try {
      return new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value));
    } catch {
      return String(value);
    }
  };

  if (startDate && endDate) return `${formatDateOnly(startDate)} to ${formatDateOnly(endDate)}`;
  return formatDateOnly(startDate) || formatDateOnly(endDate) || "-";
};

const toText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const formatCustomerUserLabel = (item) => {
  const name = toText(item?.customerUserName, "Unknown User");
  const email = String(item?.customerUserEmail ?? "").trim();
  return email ? `${name}\n${email}` : name;
};

const formatEmployeeLabel = (item) => {
  const name = toText(item?.employeeName, "Unassigned");
  const email = String(item?.employeeEmail ?? "").trim();
  return email ? `${name}\n${email}` : name;
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const getStatusTone = (status) => {
  const normalized = String(status ?? "").trim().toLowerCase();

  if (normalized === "resolved") {
    return { backgroundColor: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" };
  }

  if (normalized === "in progress") {
    return { backgroundColor: "#dbeafe", color: "#1d4ed8", borderColor: "#bfdbfe" };
  }

  if (normalized === "on hold") {
    return { backgroundColor: "#f1f5f9", color: "#475569", borderColor: "#cbd5e1" };
  }

  if (normalized === "new") {
    return { backgroundColor: "#fef3c7", color: "#92400e", borderColor: "#fde68a" };
  }

  return { backgroundColor: "#e2e8f0", color: "#334155", borderColor: "#cbd5e1" };
};

export function TicketSummaryPdfDocument({
  components,
  logoUrl,
  companyInfo,
  filters,
  report,
  selectedColumns,
  generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["ticketName", "organization", "employee", "customer", "customerUser", "openDate", "status", "taskCount"];
  const columnWidth = `${100 / activeColumns.length}%`;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 22,
      paddingBottom: 24,
      paddingHorizontal: 24,
      backgroundColor: "#f8fafc",
      fontSize: 9.5,
      color: "#0f172a",
    },
    headerBand: {
      backgroundColor: "#0f172a",
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 16,
      marginBottom: 9,
    },
    headerTopRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 4,
    },
    brandWrap: {
      flexDirection: "row",
      alignItems: "center",
      maxWidth: "62%",
    },
    logo: {
      width: 44,
      height: 44,
      objectFit: "contain",
      marginRight: 10,
      backgroundColor: "#ffffff",
      borderRadius: 10,
      padding: 4,
    },
    brandEyebrow: {
      fontSize: 8,
      color: "#94a3b8",
      letterSpacing: 0.8,
      textTransform: "uppercase",
      marginBottom: 2,
    },
    companyName: {
      fontSize: 15,
      fontWeight: 700,
      color: "#ffffff",
    },
    reportType: {
      fontSize: 9,
      color: "#e2e8f0",
    },
    reportTitle: {
      fontSize: 19,
      fontWeight: 700,
      color: "#ffffff",
      marginBottom: 4,
    },
    reportSubtitle: {
      fontSize: 9.5,
      color: "#cbd5e1",
      lineHeight: 1.5,
    },
    headerContactWrap: {
      maxWidth: "38%",
      alignItems: "flex-end",
      backgroundColor: "#111f36",
      borderWidth: 1,
      borderColor: "#24344e",
      borderRadius: 8,
      paddingVertical: 6,
      paddingHorizontal: 8,
    },
    headerContactText: {
      fontSize: 8.1,
      color: "#e2e8f0",
      lineHeight: 1.4,
      textAlign: "right",
      marginBottom: 1,
    },
    headerContactAddress: {
      fontSize: 7.8,
      color: "#cbd5e1",
      lineHeight: 1.3,
      textAlign: "right",
      marginBottom: 3,
    },
    headerContactLabel: {
      fontSize: 7.4,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 1,
    },
    cardGrid: {
      flexDirection: "row",
      marginBottom: 10,
    },
    card: {
      flexGrow: 1,
      flexBasis: 0,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 10,
      padding: 9,
      marginRight: 8,
    },
    cardLast: {
      marginRight: 0,
    },
    cardLabel: {
      fontSize: 7.5,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      marginBottom: 3,
    },
    cardValue: {
      fontSize: 13,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 1,
    },
    cardHelper: {
      fontSize: 8.4,
      color: "#475569",
      lineHeight: 1.4,
    },
    section: {
      marginBottom: 6,
    },
    sectionTitle: {
      fontSize: 11.5,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 4,
    },
    sectionNote: {
      fontSize: 8.6,
      color: "#64748b",
      lineHeight: 1.45,
      marginBottom: 6,
    },
    filterSummaryBox: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    filterSummaryText: {
      fontSize: 8.8,
      color: "#0f172a",
      lineHeight: 1.55,
    },
    table: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#0f172a",
      borderBottomWidth: 1,
      borderBottomColor: "#1e293b",
      paddingVertical: 7,
    },
    tableHeaderText: {
      fontSize: 7.6,
      fontWeight: 700,
      color: "#e2e8f0",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      paddingHorizontal: 7,
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
      paddingVertical: 6,
    },
    tableRowAlt: {
      backgroundColor: "#f8fafc",
    },
    tableRowLast: {
      borderBottomWidth: 0,
    },
    cellText: {
      fontSize: 8.1,
      color: "#0f172a",
      lineHeight: 1.4,
      paddingHorizontal: 7,
    },
    cellTextStrong: {
      fontSize: 8.1,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.4,
      paddingHorizontal: 7,
    },
    statusCell: {
      justifyContent: "center",
      alignItems: "flex-start",
      paddingHorizontal: 7,
    },
    statusBadge: {
      alignSelf: "flex-start",
      borderWidth: 1,
      borderRadius: 999,
      paddingHorizontal: 7,
      paddingVertical: 3,
    },
    statusBadgeText: {
      fontSize: 7.6,
      fontWeight: 700,
      lineHeight: 1,
    },
    emptyState: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 14,
      paddingHorizontal: 14,
    },
    emptyStateText: {
      fontSize: 9,
      color: "#475569",
      lineHeight: 1.5,
      textAlign: "center",
    },
    footer: {
      marginTop: 6,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: "#cbd5e1",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    footerText: {
      fontSize: 8,
      color: "#64748b",
    },
  });

  const filterSummaryParts = [
    `Date: ${formatDateRange(filters?.startDate, filters?.endDate)}`,
    filters?.organizationName && filters.organizationName !== "All Organizations"
      ? `Organization: ${filters.organizationName}`
      : null,
    filters?.employeeName && filters.employeeName !== "All Employees"
      ? `Employee: ${filters.employeeName}`
      : null,
    filters?.customerName && filters.customerName !== "All Customers"
      ? `Customer: ${filters.customerName}`
      : null,
    filters?.customerUserName && filters.customerUserName !== "All Customer Users"
      ? `Customer User: ${filters.customerUserName}`
      : null,
    filters?.status && filters.status !== "All Statuses"
      ? `Status: ${filters.status}`
      : null,
    filters?.customerCode && filters.customerCode !== "All"
      ? `Customer Code: ${filters.customerCode}`
      : null,
    `Generated: ${formatDateTime(generatedAt)}`,
  ].filter(Boolean);

  const columns = [
    {
      key: "ticketName",
      header: "Ticket Name",
      render: (item) => <Text style={[styles.cellText, { width: columnWidth }]}>{toText(item.ticketName)}</Text>,
    },
    {
      key: "organization",
      header: "Org:",
      render: (item) => <Text style={[styles.cellText, { width: columnWidth }]}>{toText(item.organizationName)}</Text>,
    },
    {
      key: "employee",
      header: "Employee",
      render: (item) => <Text style={[styles.cellText, { width: columnWidth }]}>{formatEmployeeLabel(item)}</Text>,
    },
    {
      key: "customer",
      header: "Customer",
      render: (item) => <Text style={[styles.cellTextStrong, { width: columnWidth }]}>{toText(item.customerName)}</Text>,
    },
    {
      key: "customerUser",
      header: "Cus:User",
      render: (item) => <Text style={[styles.cellText, { width: columnWidth }]}>{formatCustomerUserLabel(item)}</Text>,
    },
    {
      key: "openDate",
      header: "Date",
      render: (item) => <Text style={[styles.cellText, { width: columnWidth }]}>{formatDateOnly(item.openDate)}</Text>,
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <View style={[styles.statusCell, { width: columnWidth }]}>
          <View style={[styles.statusBadge, getStatusTone(item.status)]}>
            <Text style={[styles.statusBadgeText, { color: getStatusTone(item.status).color }]}>
              {toText(item.status)}
            </Text>
          </View>
        </View>
      ),
    },
    {
      key: "taskCount",
      header: "Task",
      render: (item) => <Text style={[styles.cellTextStrong, { width: columnWidth }]}>{String(item.taskCount || 0)}</Text>,
    },
  ].filter((column) => activeColumns.includes(column.key));

  return (
    <Document
      title="Organization Customer Ticket Summary Report"
      author="BanglaFire CRM"
      subject="Ticket summary report"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.headerTopRow}>
            <View style={styles.brandWrap}>
              {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
              <View>
                <Text style={styles.brandEyebrow}>Organization & Customer Tickets  Report</Text>
                <Text style={styles.companyName}>BanglaFire CRM</Text>
              </View>
            </View>
            <View style={styles.headerContactWrap}>
              <Text style={styles.headerContactAddress}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
              <Text style={styles.headerContactLabel}>Mobile</Text>
              <Text style={styles.headerContactText}>(+880) 9639222999, 1709927934</Text>
              <Text style={styles.headerContactLabel}>Email</Text>
              <Text style={styles.headerContactText}>md@banglafire.com, support@banglafire.com</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Report Filters</Text>
          <View style={styles.filterSummaryBox}>
            <Text style={styles.filterSummaryText}>{filterSummaryParts.join(" | ")}</Text>
          </View>
        </View>



        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tickets  Row</Text>

          {report?.items?.length ? (
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                {columns.map((column) => (
                  <Text key={column.key} style={[styles.tableHeaderText, { width: columnWidth }]}>
                    {column.header}
                  </Text>
                ))}
              </View>
              {report.items.map((item, index) => (
                <View
                  key={`${item.organizationId}-${item.customerId}-${item.customerUserId}-${item.employeeUserId}-${item.openDate}-${item.status}-${index}`}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 ? styles.tableRowAlt : null,
                    index === report.items.length - 1 ? styles.tableRowLast : null,
                  ]}
                  wrap={false}
                >
                  {columns.map((column) => (
                    <React.Fragment key={column.key}>
                      {column.render(item)}
                    </React.Fragment>
                  ))}
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No tickets matched the selected report filters. Adjust the filters and export again if needed.
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM Professional Report</Text>
        </View>
      </Page>
    </Document>
  );
}
